import { RegistrationsTab } from './features/leagues/tabs/RegistrationsTab';
import { PlayerProfile } from './features/players/PlayerProfile';
import { FocusedEmailPreview } from './features/leagues/FocusedEmailPreview';
import { RemovedPlayersPage } from './features/leagues/RemovedPlayersPage';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { LeagueWorkspace } from './features/leagues/LeagueWorkspace';
import { CloseOutTab } from './features/leagues/tabs/CloseOutTab';
import { EmailsTab } from './features/leagues/tabs/EmailsTab';
import { OverviewTab } from './features/leagues/tabs/OverviewTab';
import { RosterTab } from './features/leagues/tabs/RosterTab';
import { SeatingTab } from './features/leagues/tabs/SeatingTab';
import { SettingsTab } from './features/leagues/tabs/SettingsTab';
import { StandingsTab } from './features/leagues/tabs/StandingsTab';
import { LeaguesPage } from './features/leagues/LeaguesPage';
import { NewLeaguePage } from './features/leagues/NewLeaguePage';
import { PlayersList, PlayersShell } from './features/players/PlayersPage';
import { PlayerStandingsPage } from './features/player/StandingsPage';
import { TodayPage } from './features/player/TodayPage';
import { YouPage } from './features/player/YouPage';
import { ScoresTab } from './features/leagues/tabs/ScoresTab';
import { FulfillmentQueue } from './features/fulfillment/FulfillmentQueue';
import { HqDashboard } from './features/hq/HqDashboard';
import { Applications } from './features/hq/Applications';
import { HqDirectors, HqLeagues, HqMarkets, HqPlayers } from './features/hq/Directories';
import { RunOfShow } from './features/checklists/RunOfShow';
import { RunOfShowTab } from './features/leagues/tabs/RunOfShowTab';
import { IncidentsPage } from './features/incidents/IncidentsPage';
import { AccountSettings } from './features/account/AccountSettings';
import { MyKit } from './features/kit/MyKit';
import { HistoryPage } from './features/hq/HistoryPage';
import { HqSettings } from './features/hq/HqSettings';
import { NotesPage } from './features/notes/NotesPage';
import { TasksPage } from './features/tasks/TasksPage';
import { TileFleet } from './features/tiles/TileFleet';
import { KitTab } from './features/leagues/tabs/KitTab';
import { ScoreSheet } from './features/scores/ScoreSheet';
import { SubstitutesPage } from './features/players/SubstitutesPage';
import { SeatingBoard } from './features/seating/SeatingBoard';
import type { Actor } from './lib/auth';
import { homePathFor } from './lib/auth';
import type { LeagueTabKey, NavKey } from '@league/shared/contract';
import { useCapabilities } from './lib/capabilities';
import { ContactsTab } from './features/leagues/tabs/ContactsTab';
import { TrialCodes } from './features/hq/TrialCodes';
import { InstructorAccount } from './features/instructors/InstructorAccount';
import { InstructorsPage } from './features/instructors/InstructorsPage';
import { CommissionerShell } from './shells/CommissionerShell';
import { DirectorShell } from './shells/DirectorShell';
import { Playbook } from './features/checklists/Playbook';
import { EmailTemplates } from './features/hq/EmailTemplates';
import { DirectorDashboard } from './features/director/DirectorDashboard';
import { Placeholder } from './shells/Placeholder';
import { PlayerShell } from './shells/PlayerShell';

import { DIRECTOR_SCREENING_IN_V1, KIT_IN_V1 } from '@league/shared/features';

/**
 * Four surfaces behind one door. The role decides which shell you land in; the SERVER decides what
 * is inside it.
 *
 * That split is deliberate and worth stating, because it looks like authorization and isn't. These
 * routes are navigation — which rail you see, where `/` sends you. Every one of them still fetches
 * through the actor-scoped repository layer, and the API refuses a player at `/api/leagues` whether
 * or not this file ever mentions the word "player". Deleting all of it would make the app confusing
 * and would not make it insecure.
 *
 * The commissioner reaches BOTH signed-in shells, because she runs Denver as well as running
 * everything. She is the only actor who does.
 */
/**
 * The six tabs whose screens are not built. Each names the phase it belongs to and what will be
 * there, because "coming soon" tells a director nothing and an empty page reads as a bug.
 */
/** Every league tab now has a real screen. Kept as an empty list so adding one back is a two-line edit. */
const LEAGUE_TAB_PLACEHOLDERS: readonly {
  path: string;
  title: string;
  phase: string;
  what: string;
}[] = [];

export function AppRoutes({
  actor,
  playsToo = false,
  brand,
  onSignOut,
}: {
  actor: Actor;
  /** A director or the commissioner who also plays — "My games" in her menu. */
  playsToo?: boolean;
  brand: string;
  onSignOut: () => void;
}) {
  const isCommissioner = actor.role === 'commissioner';
  const isStaff = isCommissioner || actor.role === 'director';
  /*
    AN INSTRUCTOR (2026-10-16) uses the director's screens with only her own mounted: the menu and
    league tabs `/api/me` sends. A route that is not mounted falls to the catch-all and goes home, so
    typing the director's Roster address takes her back to her dashboard rather than to a screen that
    would only show refusals. (The repositories refuse her either way.)
  */
  const { nav, leagueTabs } = useCapabilities();
  const tab = (key: LeagueTabKey) => leagueTabs.includes(key);
  const menu = (key: NavKey) => nav.includes(key);

  return (
    <Routes>
      {isStaff || actor.role === 'instructor' ? (
        <Route
          path="/director"
          element={<DirectorShell actor={actor} brand={brand} onSignOut={onSignOut} playsToo={playsToo} />}
        >
          <Route index element={<DirectorDashboard actor={actor} />} />
          {menu('leagues') ? <Route path="leagues" element={<LeaguesPage />} /> : null}
          {menu('leagues') ? <Route path="leagues/archived" element={<LeaguesPage archived />} /> : null}
          {menu('leagues') ? <Route path="leagues/new" element={<NewLeaguePage />} /> : null}
          {/*
            The league workspace: ten tabs behind one route, in the order a league happens in.
            Overview, Roster, Seating and Settings are real; the rest render a bracket naming their
            phase, so the strip can be walked and judged before the screens behind it exist.
          */}
          {tab('emails') ? <Route path="leagues/:id/email-preview" element={<FocusedEmailPreview />} /> : null}
          <Route path="leagues/:id" element={<LeagueWorkspace />}>
            <Route index element={<OverviewTab />} />
            {tab('roster') ? <Route path="roster" element={<RosterTab />} /> : null}
            {tab('contacts') ? <Route path="contacts" element={<ContactsTab />} /> : null}
            {tab('registrations') ? <Route path="registrations" element={<RegistrationsTab />} /> : null}
            {tab('roster') ? <Route path="removed-players" element={<RemovedPlayersPage />} /> : null}
            {tab('seating') ? <Route path="seating" element={<SeatingTab />} /> : null}
            {tab('emails') ? <Route path="emails" element={<EmailsTab />} /> : null}
            {tab('scores') ? <Route path="scores" element={<ScoresTab />} /> : null}
            {KIT_IN_V1 && menu('kit') ? <Route path="kit" element={<KitTab />} /> : null}
            {tab('standings') ? <Route path="standings" element={<StandingsTab />} /> : null}
            {tab('close-out') ? <Route path="close-out" element={<CloseOutTab />} /> : null}
            {tab('settings') ? <Route path="settings" element={<SettingsTab />} /> : null}
            <Route path="run-of-show" element={<RunOfShowTab />} />

            {isStaff && LEAGUE_TAB_PLACEHOLDERS.map(({ path, title, phase, what }) => (
              <Route
                key={path}
                path={path}
                element={<Placeholder title={title} phase={phase} what={what} />}
              />
            ))}
          </Route>
          <Route path="sessions/:sessionId/seating" element={<SeatingBoard />} />
          <Route path="sessions/:sessionId/scores" element={<ScoreSheet actor={actor} />} />
          <Route path="sessions/:sessionId/run-of-show" element={<RunOfShow />} />

          {/*
            People, behind two tabs: everyone across this director's leagues, and the MARKET's
            substitute pool. The pool is here rather than under a league because subs are shared by
            every director in the city — and Phase 3's seating board reads it.
          */}
          {menu('players') ? <Route path="players/:playerId" element={<PlayerProfile actor={actor} />} /> : null}
          {menu('players') ? (
            <Route path="players" element={<PlayersShell />}>
              <Route index element={<PlayersList />} />
              <Route path="subs" element={<SubstitutesPage />} />
            </Route>
          ) : null}
          {/*
            The same two components the commissioner gets, with the actor passed in. That is the
            design rather than an economy: a thread rendered by two different components is how
            "I told you in week two" becomes an argument nobody can settle.
          */}
          {isStaff ? <Route path="tasks" element={<TasksPage actor={actor} />} /> : null}
          <Route path="notes" element={<NotesPage actor={actor} />} />
          {isStaff ? <Route path="incidents" element={<IncidentsPage actor={actor} />} /> : null}
          {KIT_IN_V1 && menu('kit') ? <Route path="kit" element={<MyKit />} /> : null}
          <Route path="playbook" element={<Playbook />} />
          {menu('settings') ? <Route path="settings" element={<AccountSettings onSignOut={onSignOut} />} /> : null}
          {menu('settings') ? null : <Route path="account" element={<InstructorAccount onSignOut={onSignOut} />} />}
          {menu('instructors') ? <Route path="instructors" element={<InstructorsPage />} /> : null}
        </Route>
      ) : null}

      {isCommissioner ? (
        <Route path="/hq" element={<CommissionerShell actor={actor} brand={brand} onSignOut={onSignOut} />}>
          {KIT_IN_V1 ? <Route path="fulfillment" element={<FulfillmentQueue />} /> : null}
          {KIT_IN_V1 ? <Route path="tiles" element={<TileFleet />} /> : null}
          <Route path="settings" element={<HqSettings />} />
          <Route path="trial-codes" element={<TrialCodes />} />
          <Route path="tasks" element={<TasksPage actor={actor} />} />
          <Route path="notes" element={<NotesPage actor={actor} />} />
          <Route path="quality" element={<IncidentsPage actor={actor} />} />
          <Route path="audit" element={<HistoryPage actor={actor} />} />
          <Route path="leagues" element={<HqLeagues />} />
          <Route path="markets" element={<HqMarkets />} />
          <Route path="directors" element={<HqDirectors />} />
          <Route path="players/:playerId" element={<PlayerProfile actor={actor} />} />
          <Route path="players" element={<HqPlayers />} />
          {DIRECTOR_SCREENING_IN_V1 ? <Route path="applications" element={<Applications />} /> : null}
          {/*
            The action queue links here. It is the DIRECTOR's workspace under the bonnet — the same
            screen, the same checks, the same audit trail — so this hands over rather than growing a
            commissioner-only copy of ten tabs that would drift from the original.
          */}
          <Route path="leagues/:id" element={<ToDirectorWorkspace />} />
          <Route index element={<HqDashboard />} />
          <Route path="playbook" element={<Playbook showVersions />} />
          <Route path="templates" element={<EmailTemplates />} />
          {HQ_PLACEHOLDERS.map(({ path, title, phase, what }) => (
            <Route
              key={path}
              path={path}
              element={<Placeholder title={title} phase={phase} what={what} />}
            />
          ))}
        </Route>
      ) : null}

      <Route path="/my" element={<PlayerShell actor={actor} brand={brand} />}>
        <Route index element={<TodayPage />} />
        <Route path="standings" element={<PlayerStandingsPage />} />
        <Route path="you" element={<YouPage onSignOut={onSignOut} />} />
      </Route>

      {/*
        Anything else goes to whichever app this actor belongs in. A director who bookmarks `/hq`
        lands on their own dashboard rather than a 403 page — the API already refused them, and a
        dead end teaches nothing.
      */}
      <Route path="*" element={<Navigate to={homePathFor(actor)} replace />} />
    </Routes>
  );
}

/**
 * Every HQ route now has a real screen. Kept as an empty list, like the league tabs above, so that
 * adding an unbuilt route back is a two-line edit rather than a re-import.
 */
const HQ_PLACEHOLDERS: readonly {
  path: string;
  title: string;
  phase: string;
  what: string;
}[] = [];



/**
 * `/hq/leagues/:id` is the commissioner's way into one league, and it is the director's workspace.
 *
 * A redirect rather than a second mount of ten nested tabs: she passes every authorization check a
 * director does, so it is literally the same screen, and two copies of it would drift.
 */
function ToDirectorWorkspace() {
  const { id } = useParams();
  return <Navigate to={`/director/leagues/${id}`} replace />;
}
